import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { Task } from '../../types/tasks';
import { Clock, AlertCircle, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

export default function TasksWidget() {
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    // Subscribe to tasks assigned to the current user
    const unsubscribe = onSnapshot(
      query(
        collection(db, 'tasks'),
        where('assignedTo', 'array-contains', user.uid),
        orderBy('createdAt', 'desc'),
        limit(3)
      ),
      (snapshot) => {
        const taskData: Task[] = [];
        snapshot.forEach((doc) => {
          taskData.push({ id: doc.id, ...doc.data() } as Task);
        });
        setTasks(taskData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading tasks:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  const getIcon = (task: Task) => {
    switch (task.status) {
      case 'completed':
        return <CheckCircle className="w-5 h-5 text-success-DEFAULT" />;
      case 'in-progress':
        return <Clock className="w-5 h-5 text-warning-DEFAULT" />;
      default:
        return <AlertCircle className="w-5 h-5 text-error-DEFAULT" />;
    }
  };

  if (loading) {
    return (
      <div className="bg-surface-light rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-text-primary">Tasks</h2>
          <Link 
            to="/tasks"
            className="text-sm text-primary-500 hover:text-primary-600 font-medium"
          >
            View All
          </Link>
        </div>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="animate-pulse flex items-start gap-4 p-4 rounded-lg bg-background"
            >
              <div className="w-5 h-5 bg-text-muted rounded-full" />
              <div className="flex-1">
                <div className="h-4 bg-text-muted rounded w-3/4 mb-2" />
                <div className="h-3 bg-text-muted rounded w-1/4" />
              </div>
              <div className="w-16 h-6 bg-text-muted rounded-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface-light rounded-xl shadow-sm p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-text-primary">Tasks</h2>
        <Link 
          to="/tasks"
          className="text-sm text-primary-500 hover:text-primary-600 font-medium"
        >
          View All
        </Link>
      </div>

      <div className="space-y-4">
        {tasks.map((task) => (
          <div
            key={task.id}
            onClick={() => navigate('/tasks')}
            className="flex items-start gap-4 p-4 rounded-lg bg-background hover:bg-primary-50 
                     transition-colors cursor-pointer"
          >
            {getIcon(task)}

            <div className="flex-1">
              <p className="text-sm text-text-primary font-medium hover:text-primary-600">
                {task.title}
              </p>
              <span className="text-xs text-text-muted">
                {task.dueDate 
                  ? format(new Date(task.dueDate), 'MMM d, h:mm a')
                  : 'No due date'
                }
              </span>
            </div>

            <span
              className={`px-2 py-1 rounded-full text-xs font-medium
                ${task.priority === 'high' 
                  ? 'bg-error-light/20 text-error-dark'
                  : task.priority === 'medium'
                  ? 'bg-warning-light/20 text-warning-dark'
                  : 'bg-success-light/20 text-success-dark'
                }`}
            >
              {task.priority}
            </span>
          </div>
        ))}

        {tasks.length === 0 && (
          <div className="text-center py-4">
            <p className="text-text-secondary">No tasks assigned</p>
          </div>
        )}
      </div>
    </div>
  );
}